/*
 * File: PaymentSchema.ts
 * Author: Austin Robinson
 * Date Created: 01/16/2025
 * Copyright (c) DrivingDoc 2025
 */

import { formatCents } from "utils/formatCurrency";
import { z } from "zod";

export const PaymentAllocationSchema = z.object({
  // id of the charge to allocate to
  id: z.string().uuid().readonly(),

  // amount to allocate towards charge
  amount: z
    .number({
      required_error: "Amount is required",
      invalid_type_error: "Expected a number",
    })
    .nonnegative()
    .nullable()
    .default(null),

  /* read only fields for display/validation purposes */

  // amount previously allocated towards charge
  previous_amount: z.number().nonnegative().default(0).readonly(),

  // charge memo
  memo: z.string().readonly(),

  // charge amount after discounts
  discounted_total: z.number().nonnegative().readonly(),

  // charge deposit
  deposit: z.number().nonnegative().readonly(),

  // unpaid amount
  remaining: z.number().nonnegative().readonly(),

  // charge due date
  discounted_total_due: z
    .string()
    .datetime()
    .nullable()
    .default(null)
    .readonly(),

  // deposit due date
  deposit_due: z.string().datetime().nullable().default(null).readonly(),
});

export const BasePaymentFormSchema = z.object({
  // id of the payment (null for new payments)
  id: z.string().uuid().nullable().default(null).readonly(),

  // id of the student the payment belongs to
  student_user_id: z.string().uuid().readonly(),

  // the date the payment was received (might not be the date the payment is recorded)
  date: z
    .string({
      required_error: "Payment date is required",
    })
    .datetime()
    .default(() => new Date().toISOString()),

  // total payment amount
  amount: z
    .number({
      required_error: "Amount is required",
      invalid_type_error: "Expected a number",
    })
    .positive(),

  // total amount refunded from this payment
  refunded: z.number().nonnegative().default(0).readonly(),

  // memo to briefly describe the payment
  memo: z
    .string({
      required_error: "Memo is required",
    })
    .min(1),

  // longer description for the payment
  description: z.string().nullable().default(null),

  // private notes that are only visible to administrators
  private_notes: z.string().nullable().default(null),

  // id of the payment method
  payment_method_id: z
    .string({ required_error: "Payment method is required" })
    .uuid(),

  // optional field to record a check number
  check_no: z.string().nullable().default(null),

  // optional field to record who the payment was received from
  received_from: z.string().nullable().default(null),

  // list of charges this payment will be applied to along with the amount to apply
  allocations: z
    .array(PaymentAllocationSchema)
    .default([])
    .superRefine((data, ctx) => {
      // ensure each allocation does not exceed the remaining balance for the related charge
      data.forEach((allocation, index) => {
        // calculate the maximum amount that can be allocated, accounting for any previous allocation being replaced
        const maxAllocatable =
          allocation.remaining + (allocation.previous_amount ?? 0);

        // new amount to allocate to the charge
        const requested = allocation.amount ?? 0;

        // if the requested amount exceeds the max allocable amount
        if (requested > maxAllocatable) {
          // add an issue to the amount field for the current allocation
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [index, "amount"],
            message:
              "Amount cannot exceed the remaining balance for this charge.",
          });
        }
      });
    }),
});

export const PaymentFormSchema = BasePaymentFormSchema.superRefine(
  (data, ctx) => {
    // calculate the total allocated amount
    const totalAllocated = data.allocations.reduce(
      (sum, allocation) => sum + (allocation.amount ?? 0),
      0
    );

    // calculate the maximum amount that can be allocated
    const maxAllocatable = data.amount - data.refunded;

    // if the total allocated amount exceeds the total payment amount
    if (totalAllocated > maxAllocatable) {
      // add an issue to the allocations field
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["allocations"],
        message: `You allocated ${formatCents(totalAllocated)}, which exceeds the total unrefunded payment amount of ${formatCents(maxAllocatable)}.`,
      });
    }
  }
);

export type PaymentFormValues = z.infer<typeof PaymentFormSchema>;
